import { useState } from "react";
import { transcriptFileName, transcriptHtml, type TranscriptDocument } from "./transcriptExport";

/**
 * KEY-591: "Download" and "Print" for a discussion. `build` assembles the document from what the reader already has
 * on screen at the moment they press, so what is exported is exactly what they can see.
 */
export function TranscriptExportButtons({ build, disabled }: { build: () => TranscriptDocument | null; disabled?: boolean }) {
  const [error, setError] = useState<string | null>(null);

  function download() {
    const doc = build();
    if (!doc) return;
    setError(null);
    try {
      const blob = new Blob([transcriptHtml(doc)], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = transcriptFileName(doc.title);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setError("The download didn't start — try again.");
    }
  }

  function print() {
    const doc = build();
    if (!doc) return;
    setError(null);
    // Opened synchronously inside the click, so a popup blocker treats it as the user's own action.
    const win = window.open("", "_blank");
    if (!win) {
      setError("Your browser blocked the print window — allow pop-ups for this site, or use Download.");
      return;
    }
    win.document.open();
    win.document.write(transcriptHtml(doc));
    win.document.close();
    win.focus();
    // Give the new page a moment to lay out before the print dialog snapshots it.
    setTimeout(() => win.print(), 250);
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <button type="button" onClick={download} disabled={disabled} className="kd-focusable" style={btn} title="Download this discussion as a page you can keep, print or open in Word">
        Download
      </button>
      <button type="button" onClick={print} disabled={disabled} className="kd-focusable" style={btn} title="Print this discussion, or save it as a PDF">
        Print
      </button>
      {error && (
        <span role="alert" style={{ fontSize: 12.5, color: "var(--st-overdue, #c11118)" }}>
          {error}
        </span>
      )}
    </div>
  );
}

const btn = {
  font: "inherit",
  fontSize: 12.5,
  fontWeight: 600,
  padding: "4px 12px",
  borderRadius: 999,
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface)",
  color: "var(--text-muted)",
  cursor: "pointer",
} as const;
